import { MAX_PIXABAY_PAGES, PIXABAY_API_KEY } from "@/consts";
import type { IPixabayImage, IPixabayResult } from "@/interfaces";
import { sentryException } from "@/sentry";
import { useAuthStore } from "@/stores/authStore";
import { simpleError } from "@/stories/displays/SimpleError/SimpleError.vue";
import { computed, ref } from "vue";
import type { IFileState } from "@/interfaces";
import converters from "@/converters";

const page = ref(1);
const selectedImages = ref<string[]>([]);
const loading = ref<string | null>(null);
const searchText = ref("");
const searchResult = ref<IPixabayImage[]>([]);
let abortController: AbortController | null = null;

export default function usePixabayDialog() {
  const fetchImages = async (event?: Event) => {
    const authStore = useAuthStore();

    event?.preventDefault();

    // Cancel any in-flight request so stale responses never append to results.
    // This must happen before the empty-string guard so clearing the search box
    // always cancels an in-flight request.
    abortController?.abort();
    abortController = null;

    if (searchText.value.trim().length === 0) {
      return;
    }

    const myController = new AbortController();
    abortController = myController;

    try {
      loading.value = "templates.pixabay.loading_message";

      const url = new URL("https://pixabay.com/api/");
      url.searchParams.append("key", PIXABAY_API_KEY);
      url.searchParams.append("q", searchText.value);
      url.searchParams.append("lang", authStore.institutionLocale);
      url.searchParams.append("image_type", "photo");
      url.searchParams.append("min_width", "200");
      url.searchParams.append("min_height", "200");
      url.searchParams.append("page", page.value.toString());
      url.searchParams.append("per_page", "16");
      const response = await fetch(url, { signal: myController.signal });
      const result: IPixabayResult = await response.json();

      // Pixabay returns the same images in different pages sometimes.
      const currentIds = searchResult.value.map((item) => item.id);
      const filteredItems = result.hits.filter(
        (item) => !currentIds.includes(item.id),
      );

      searchResult.value.push(...filteredItems);
      return searchResult.value;
    } catch (error) {
      if (error instanceof DOMException && error.name === "AbortError") {
        return;
      }
      sentryException(error);
      simpleError("global.error_api");
    } finally {
      // Only the latest request clears the loading indicator so an aborted
      // request doesn't blank it while the newer request is still in-flight.
      if (abortController === myController) {
        loading.value = null;
      }
    }
  };

  const loadNextPage = async () => {
    if (loading.value) {
      return;
    }

    if (page.value >= MAX_PIXABAY_PAGES) {
      loading.value = null;
      return;
    }

    page.value++;
    return await fetchImages();
  };

  const reset = () => {
    abortController?.abort();
    abortController = null;
    page.value = 1;
    selectedImages.value = [];
    loading.value = null;
    searchText.value = "";
    searchResult.value = [];
  };

  const toggleImages = (image: IFileState) => {
    if (!image.id) {
      return;
    }

    if (selectedImages.value.includes(image.id)) {
      selectedImages.value = selectedImages.value.filter(
        (id) => image.id !== id,
      );
    } else {
      selectedImages.value.push(image.id);
    }
  };

  const images = computed<IFileState[]>(() => {
    return converters.pixabay.toFileState(searchResult.value);
  });

  return {
    page,
    selectedImages,
    loading,
    searchText,
    searchResult,
    images,
    fetchImages,
    loadNextPage,
    reset,
    toggleImages,
  };
}
